<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col :xl="isWide ? 12 : 7" :lg="isWide ? 12 : 6" class="transition-all">
        <b-card no-body class="border-0 shadow-sm mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div>
              <i class="icon-screen-desktop"></i> <strong>{{ title }}</strong>
            </div>
            <div class="card-header-actions">
              <b-button @click="toggleWide" variant="link" class="card-header-action btn-setting p-0 mr-3 text-muted">
                <i class="fa" :class="isWide ? 'fa-compress' : 'fa-expand'"></i>
              </b-button>
              <b-button @click="saveNews" variant="primary" size="sm" :disabled="!hasPermission(editPermission) || loading">
                <i class="fa fa-save mr-1" v-if="!loading"></i>
                <i class="fa fa-spinner fa-spin mr-1" v-else></i>
                {{ loading ? 'Saving...' : 'Save Changes' }}
              </b-button>
            </div>
          </div>

          <!-- Professional Toolbar -->
          <div class="p-3 border-bottom">
            <div class="d-flex flex-wrap align-items-center">
              <div class="toolbar-section mr-4">
                <small class="text-muted d-block mb-2 font-weight-bold">COLORS</small>
                <div class="d-flex flex-wrap gap-1">
                  <div v-for="color in quickColors" :key="color.hex"
                       class="color-swatch"
                       @click="insertTag(`[${color.hex}]`, '[-]')"
                       :style="{ backgroundColor: '#' + color.hex }"
                       v-b-tooltip.hover :title="color.name">
                  </div>
                </div>
              </div>

              <div class="toolbar-divider mx-2 d-none d-md-block"></div>

              <div v-if="showContextTools" class="toolbar-section mr-4">
                <small class="text-muted d-block mb-2 font-weight-bold">FORMATTING TOOLS</small>
                <b-button-group size="sm">
                  <template v-if="isPvp">
                    <b-button @click="insertTag('[FFAE00]', '[-]')" variant="outline-warning" title="Header (Orange)">
                      <i class="fa fa-header"></i>
                    </b-button>
                    <b-button @click="insertTag('[309be5]', '[-]')" variant="outline-info" title="Keyword (Blue)">
                      <i class="fa fa-tag"></i>
                    </b-button>
                    <b-button @click="insertTag('[C05B2B]', '.[-]')" variant="outline-danger" title="Rank Number (Bronze)">
                      <i class="fa fa-list-ol"></i>
                    </b-button>
                    <b-button @click="insertTag('[cc0000]', '[-]')" variant="outline-danger" title="Warning (Red)">
                      <i class="fa fa-exclamation-triangle"></i>
                    </b-button>
                  </template>
                  <template v-if="isNews">
                    <b-button @click="insertTag('[FFB600]', '[-]')" variant="outline-warning" title="Date (Gold)">
                      <i class="fa fa-calendar"></i>
                    </b-button>
                    <b-button @click="insertTag('[13AA00]', '[-]')" variant="outline-success" title="Location (Green)">
                      <i class="fa fa-map-marker"></i>
                    </b-button>
                    <b-button @click="insertTag('[FF9200]', '[-]')" variant="outline-warning" title="Item/Mount (Orange)">
                      <i class="fa fa-gift"></i>
                    </b-button>
                    <b-button @click="insertTag('[FF00C8]', '[-]')" variant="outline-primary" title="Event/Quest (Pink)">
                      <i class="fa fa-star"></i>
                    </b-button>
                  </template>
                </b-button-group>
              </div>

              <div v-if="showContextTools" class="toolbar-divider mx-2 d-none d-md-block"></div>

              <div class="toolbar-section mr-4">
                <small class="text-muted d-block mb-2 font-weight-bold">ELEMENTS</small>
                <b-button-group size="sm">
                  <b-button @click="insertTagTop('[u=URL]', '[/u]')" variant="outline-secondary">
                    <i class="fa fa-link mr-1"></i> Link
                  </b-button>
                  <b-button @click="insertTag('[6E6E6E]***********************************[-]\n', '')" variant="outline-secondary">
                    <i class="fa fa-minus mr-1"></i> Separator
                  </b-button>
                </b-button-group>
              </div>

              <div class="toolbar-divider mx-2 d-none d-md-block"></div>

              <div class="toolbar-section">
                <small class="text-muted d-block mb-2 font-weight-bold">TEMPLATES</small>
                <b-dropdown variant="outline-secondary" size="sm" text="Quick Templates">
                  <b-dropdown-item @click="applyTemplate('date')">New Date</b-dropdown-item>
                  <b-dropdown-item @click="applyTemplate('patch')">Patch Notes</b-dropdown-item>
                  <b-dropdown-item @click="applyTemplate('event')">Event News</b-dropdown-item>
                </b-dropdown>
              </div>

              <div class="ml-auto text-right align-self-end">
                <small class="text-muted font-weight-bold">{{ content.length }} chars</small>
              </div>
            </div>
          </div>

          <div class="card-body p-0 editor-body">
            <textarea
              ref="editor"
              class="form-control editor-field"
              v-model="content"
              placeholder="Enter the content here..."
              spellcheck="false"
            ></textarea>
          </div>
        </b-card>
      </b-col>

      <!-- Ingame Preview Column -->
      <b-col xl="5" lg="6" v-show="!isWide" class="transition-all">
        <b-card no-body class="border-0 shadow-sm h-100">
          <div class="card-header">
            <i class="fa fa-eye"></i> <strong>Live Preview</strong>
          </div>
          <div class="card-body bg-gray-100 p-4">

            <div class="game-mockup shadow">
              <div class="game-mockup-header px-3 py-2 d-flex justify-content-between align-items-center">
                <span class="small font-weight-bold text-muted">PREVIEW</span>
                <div class="window-controls">
                  <span class="w-dot"></span><span class="w-dot"></span><span class="w-dot"></span>
                </div>
              </div>
              <div class="game-mockup-body scroll-ingame">
                <div class="ingame-content" v-html="parsedContent"></div>
              </div>
            </div>

            <div class="mt-4">
              <b-alert show variant="info" class="small mb-0 py-2">
                <i class="fa fa-info-circle mr-1"></i>
                Content is cached globally and served via Cloudflare.
              </b-alert>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import VueNotifications from 'vue-notifications'
  import { mapGetters } from 'vuex'
  import moment from 'moment'

  export default {
    name: 'InfoTextEditor',
    props: {
      textKey: {
        type: String,
        required: true
      },
      title: {
        type: String,
        default: 'Edit Content'
      },
      viewPermission: {
        type: [String, Array],
        required: true
      },
      editPermission: {
        type: [String, Array],
        required: true
      }
    },
    computed: {
      ...mapGetters({
        InfoText: 'InfoText',
        isConnected: 'isConnected'
      }),
      loadedContent() {
        return this.InfoText(this.textKey);
      },
      parsedContent() {
        if (!this.content) return '<div class="text-center text-muted p-5 mt-5 italic">Preview is empty</div>';

        let html = this.content
          .replace(/\r\n?/g, "\n")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>");

        // Ingame Tag Parser [HEX]...[-]
        html = html.replace(/\x5B([A-Fa-f0-9]{6})\x5D(.*?)(?=\x5B[A-Fa-f0-9]{6}\x5D|\x5B-\x5D|$)/g, (match, hex, text) => {
           let cleanText = text.replace(/\x5B-\x5D$/, '');
           return `<span style="color: #${hex}; text-shadow: 1px 1px 1px rgba(0,0,0,0.8);">${cleanText}</span>`;
        });

        html = html.replace(/\x5Bu=(.*?)\x5D(.*?)(\x5B-\x5D|\x5B\/u\x5D)/g, (match, url, text) => {
          return `<a href="${url}" target="_blank" style="color: #28eefc; text-shadow: 0 0 2px rgba(40, 238, 252, 0.3); text-decoration: underline;">${text}</a>`;
        });

        html = html.replace(/\x5B-\x5D/g, '');

        const urlRegex = /(^|[\s([])(https?:\/\/[^\s<]+)/g;
        const parts = html.split(/(<a\b[^>]*>.*?<\/a>)/gi);
        html = parts.map((part) => {
          if (part.toLowerCase().startsWith('<a '))
            return part;
          return part.replace(urlRegex, (match, prefix, url) => {
            let clean = url;
            let trailing = '';
            while (clean.length > 0 && '.,)]}'.includes(clean[clean.length - 1])) {
              trailing = clean[clean.length - 1] + trailing;
              clean = clean.slice(0, -1);
            }
            return `${prefix}<a href="${clean}" target="_blank" style="color: #28eefc; text-shadow: 0 0 2px rgba(40, 238, 252, 0.3); text-decoration: underline;">${clean}</a>${trailing}`;
          });
        }).join('');

        return html;
      },
      isPvp() {
        return this.textKey && this.textKey.toLowerCase().includes('pvp');
      },
      isNews() {
        return this.textKey && (this.textKey.toLowerCase().includes('news') || this.textKey.toLowerCase().includes('ingame'));
      },
      showContextTools() {
        return this.isPvp || this.isNews;
      }
    },
    data () {
      return {
        content: '',
        loading: false,
        isWide: false,
        quickColors: [
          { name: 'PRO Gold', hex: 'FFAE00' },
          { name: 'PRO Blue', hex: '309be5' },
          { name: 'Rank Bronze', hex: 'C05B2B' },
          { name: 'Link Cyan', hex: '28eefc' },
          { name: 'Warning Red', hex: 'cc0000' },
          { name: 'Platinum', hex: 'D0F6FF' },
          { name: 'Status Red', hex: 'e74c3c' },
          { name: 'Date Gold', hex: 'FFB600' },
          { name: 'Location Green', hex: '13AA00' },
          { name: 'Item Orange', hex: 'FF9200' },
          { name: 'Event Pink', hex: 'FF00C8' },
          { name: 'Purple Title', hex: '7b55d3' },
          { name: 'Grey', hex: 'C3C3C3' },
          { name: 'White', hex: 'FFFFFF' }
        ]
      }
    },
    notifications: {
      SavedNews: {
        type: VueNotifications.types.success,
        title: 'Saved',
        message: 'The content has been updated successfully.'
      }
    },
    created () {
      this.$socket.sendObj({event: 'getInfoText', data: JSON.stringify(this.textKey)})
    },
    watch: {
      loadedContent: {
        immediate: true,
        handler (val) {
          if (val) {
            this.content = val
          }
        }
      }
    },
    methods: {
      saveNews () {
        this.loading = true;
        this.$socket.sendObj({
          event: 'saveInfoText',
          data: JSON.stringify({
            key: this.textKey,
            content: this.content
          })
        })
        setTimeout(() => {
          this.loading = false;
          // Actual success notification should ideally come from backend ack,
          // but for now we follow existing pattern (assuming success if no error immediately)
          // Actually backend sends "sendSystemMessage" with success.
          // But here we use local notification too?
          // The existing Ingame.vue used local notification.
          // However, backend also sends system message.
          // I'll keep it consistent with Ingame.vue for now.
        }, 600);
      },
      toggleWide() {
        this.isWide = !this.isWide;
      },
      applyTemplate(type) {
        const dateStr = moment().format('MMMM Do, YYYY');
        if (type === 'date') {
          this.insertTag(`[FFB600]${dateStr}[-]
`, '');
        } else if (type === 'patch') {
          this.content = `[FFB600]${dateStr}[-]

[7b55d3]Patch Notes[-]

- Improved system performance
- Fixed various issues

[6E6E6E]***********************************[-]
` + this.content;
        } else if (type === 'event') {
          this.content = `[FFB600]${dateStr}[-]

[7b55d3]New Event![-]

Join the latest event and win rewards!
[28eefc][u=https://pokemonrevolution.net]Forum Link[/u][-]

[6E6E6E]***********************************[-]
` + this.content;
        }
      },
      insertTag(startTag, endTag) {
        const textarea = this.$refs.editor;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const before = text.substring(0, start);
        const middle = text.substring(start, end);
        const after = text.substring(end);

        this.content = before + startTag + middle + (endTag || '') + after;

        this.$nextTick(() => {
          textarea.focus();
          let newPos = start + startTag.length + (middle.length > 0 ? middle.length + (endTag ? endTag.length : 0) : 0);
          textarea.setSelectionRange(newPos, newPos);
        });
      },
      insertTagTop(startTag, endTag) {
        const textarea = this.$refs.editor;
        const separator = this.content.length > 0 ? '\n\n' : '';
        this.content = startTag + (endTag || '') + separator + this.content;

        this.$nextTick(() => {
          textarea.focus();
          const cursorPos = startTag.length;
          textarea.setSelectionRange(cursorPos, cursorPos);
        });
      }
    }
  }
</script>

<style scoped lang="scss">
/* Professional CoreUI Spacing & Style */
.transition-all { transition: all 0.3s ease-in-out; }

.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  cursor: pointer;
  border: 1px solid rgba(0,0,0,0.1);
  display: inline-block;
  transition: transform 0.1s;
  &:hover { transform: scale(1.15); z-index: 2; border-color: #fff; }
}

.toolbar-divider {
  width: 1px;
  height: 30px;
  background-color: #dee2e6;
}

.editor-field {
  width: 100%;
  min-height: 600px;
  border: 1px solid #3e515b;
  border-radius: 0;
  padding: 25px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  background-color: #1f2428;
  color: #f0f1f3;
  &:focus { outline: none; box-shadow: none; }
}

.editor-body {
  background-color: #23282c;
}

.editor-field::placeholder {
  color: #93a1ab;
}

/* Ingame Mockup */
.game-mockup {
  background-color: #000;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  height: 550px;
  overflow: hidden;
  border: 1px solid #2f353a;
}

.game-mockup-header {
  background-color: #23282c;
  border-bottom: 1px solid #000;
}

.window-controls {
  display: flex;
  gap: 4px;
  .w-dot { width: 6px; height: 6px; border-radius: 50%; background: #444; }
}

.game-mockup-body {
  padding: 30px;
  overflow-y: auto;
  flex-grow: 1;
  background: radial-gradient(circle at top left, #1c1f22 0%, #050505 100%);
}

.ingame-content {
  color: #f0f1f3;
  font-size: 15px;
  line-height: 1.6;
  font-family: 'Segoe UI', Arial, sans-serif;
  white-space: pre-wrap;
}

/* Transparent Ingame Scrollbar */
.scroll-ingame {
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    border: 3px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.2); border: 3px solid transparent; background-clip: content-box; }
}

/* Standard Scrollbar for Editor */
.scroll-dark {
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; }
  &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #bbb; }
}

.gap-1 { gap: 0.25rem; }
.italic { font-style: italic; }
</style>
